import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Heart, 
  Moon, 
  Scissors, 
  Users, 
  Shield, 
  Truck, 
  BookOpen,
  Award,
  CheckCircle,
  Activity,
  Stethoscope,
  Building2
} from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';

const ServicesSection: React.FC = () => {
  const { t } = useLanguageStore();

  const primaryServices = [
    {
      icon: Clock,
      title: '24/7 OPD Service',
      titleSinhala: '24/7 OPD සේවාව',
      description: 'Round-the-clock outpatient department services',
      descriptionSinhala: 'පැය 24 පුරා බාහිර රෝගී දෙපාර්තමේන්තු සේවාවන්',
      category: 'essential'
    },
    {
      icon: Heart,
      title: 'All Patient Care',
      titleSinhala: 'සියලු රෝගී සත්කාර',
      description: 'Comprehensive healthcare for all patients',
      descriptionSinhala: 'සියලු රෝගීන් සඳහා සම්පූර්ණ සෞඛ්‍ය සත්කාර',
      category: 'essential'
    },
    {
      icon: Moon,
      title: 'Night Medical Clinic',
      titleSinhala: 'රාත්‍රී වෛද්‍ය සායනය',
      description: 'Tuesday at 7PM for employees, businessmen & farmers',
      descriptionSinhala: '',
      category: 'specialized'
    },
    {
      icon: Activity,
      title: 'Night HLC Clinic (NCD)',
      titleSinhala: 'රාත්‍රී HLC සායනය (NCD)',
      description: 'Thursday at 7PM for non-communicable diseases',
      descriptionSinhala: 'බ්‍රහස්පතිඳා සවස 7 ට නොසැරයන රෝග සඳහා',
      category: 'specialized'
    },
    {
      icon: Scissors,
      title: 'Mini Theatre',
      titleSinhala: 'කුඩා ශල්‍යාගාරය',
      description: 'Minor surgery every Saturday 8AM-12PM',
      descriptionSinhala: 'සෑම සෙනසුරාදා 8-12 දක්වා කුඩා ශල්‍යකර්ම',
      category: 'specialized'
    },
    {
      icon: Stethoscope,
      title: 'Daily HLC Clinic (NCD)',
      titleSinhala: 'දෛනික HLC සායනය (NCD)',
      description: 'Daily non-communicable disease management',
      descriptionSinhala: 'දෛනික නොසැරයන රෝග කළමනාකරණය',
      category: 'essential'
    }
  ];

  const supportServices = [
    {
      icon: Building2,
      title: 'Health Promotion Unit',
      titleSinhala: 'සෞඛ්‍ය ප්‍රවර්ධන ඒකකය',
      category: 'support'
    },
    {
      icon: Shield,
      title: 'CSSD Unit',
      titleSinhala: 'CSSD ඒකකය',
      category: 'support'
    },
    {
      icon: Users,
      title: 'Public Health Nursing Officer Service',
      titleSinhala: 'මහජන සෞඛ්‍ය හෙද නිලධාරී සේවාව',
      category: 'support'
    },
    {
      icon: BookOpen,
      title: 'Clinic Health Education Service',
      titleSinhala: 'සායන සෞඛ්‍ය අධ්‍යාපන සේවාව',
      category: 'support'
    },
    {
      icon: Truck,
      title: 'On Call Ambulance Service',
      titleSinhala: 'ඇමතුම මත ගිලන් රථ සේවාව',
      category: 'support'
    },
    {
      icon: CheckCircle,
      title: 'Daily Registration Bar Code System',
      titleSinhala: 'දෛනික ලියාපදිංචි බාර් කෝඩ් ක්‍රමය',
      category: 'support'
    }
  ];

  const uniqueValues = [
    'Transparency', 'Accountability', 'Flexibility', 
    'Friendliness', '"Suwa Mithuru" Committee', 
    'Second Line Leadership', 'Duk Ganavily Committee'
  ];

  const uniqueValuesSinhala = [
    'පාරදර්ශිතාව', 'වගකීම', 'නම්‍යශීලීත්වය',
    'මිත්‍රශීලීත්වය', '"සුව මිතුරු" කමිටුව',
    'දෙවන පෙළ නායකත්වය', 'දුක් ගනවිලි කමිටුව'
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-warm-white via-blue-50/30 to-green-50/30 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="services-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="1" fill="currentColor" className="text-primary-green"/>
              <rect x="8" y="8" width="4" height="4" fill="currentColor" className="text-secondary-blue" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#services-pattern)"/>
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-8 md:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-4 md:mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-primary-green to-secondary-blue rounded-2xl flex items-center justify-center shadow-lg">
              <Award className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-text-dark">
              {t('our-services', 'Our Services', 'අපගේ සේවාවන්')}
            </h2>
          </div>
          <div className="w-24 md:w-32 h-1 bg-gradient-to-r from-primary-green via-accent-gold to-secondary-blue mx-auto mb-4 md:mb-6 rounded-full" />
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed px-4">
            {t(
              'services-intro',
              'Comprehensive healthcare services delivered with excellence, compassion, and dedication to serve our community 24/7.',
              'විශිෂ්ටත්වය, කරුණාව සහ අපගේ ප්‍රජාවට 24/7 සේවා කිරීමේ කැපවීම සහිතව සම්පූර්ණ සෞඛ්‍ය සේවාවන්.'
            )}
          </p>
        </motion.div>

        {/* Primary Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 md:mb-12 lg:mb-16"
        >
          <h3 className="text-xl sm:text-2xl font-bold text-text-dark mb-6 md:mb-8 text-center">
            {t('primary-services', 'Primary Medical Services', 'ප්‍රාථමික වෛද්‍ය සේවාවන්')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {primaryServices.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary-green/30 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start gap-3 md:gap-4">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0 ${
                    service.category === 'essential' 
                      ? 'bg-gradient-to-br from-primary-green to-green-600' 
                      : 'bg-gradient-to-br from-secondary-blue to-blue-600'
                  }`}>
                    <service.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base md:text-lg font-semibold text-text-dark mb-1 md:mb-2 group-hover:text-primary-green transition-colors duration-300">
                      {t(`service-${service.title.toLowerCase().replace(/\s+/g, '-')}`, service.title, service.titleSinhala)}
                    </h4>
                    {service.description && (
                      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                        {t(`service-desc-${service.title.toLowerCase().replace(/\s+/g, '-')}`, service.description, service.descriptionSinhala)}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Support Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-8 md:mb-12 lg:mb-16"
        >
          <h3 className="text-xl sm:text-2xl font-bold text-text-dark mb-6 md:mb-8 text-center">
            {t('support-services', 'Support & Administrative Services', 'සහාය සහ පරිපාලන සේවාවන්')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {supportServices.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-gradient-to-r from-white to-blue-50/50 rounded-lg md:rounded-xl p-3 md:p-4 border border-blue-100 hover:border-secondary-blue/40 transition-all duration-300 group"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <service.icon className="w-6 h-6 md:w-8 md:h-8 text-secondary-blue group-hover:text-primary-green transition-colors duration-300 flex-shrink-0" />
                  <span className="text-sm md:text-base font-medium text-text-dark group-hover:text-primary-green transition-colors duration-300 break-words">
                    {t(`support-${service.title.toLowerCase().replace(/\s+/g, '-')}`, service.title, service.titleSinhala)}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Special Implementations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-r from-primary-green/10 via-accent-gold/10 to-secondary-blue/10 rounded-3xl p-8 border border-primary-green/20">
            <h3 className="text-2xl font-bold text-text-dark mb-6 text-center">
              {t('special-implementations', 'Special Implementations', 'විශේෂ ක්‍රියාත්මක කිරීම්')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/60 rounded-xl p-6 border border-white/40">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-6 h-6 text-primary-green" />
                  <h4 className="font-semibold text-text-dark">
                    {t('minimata-concept', 'Implementation of Minimata Concept', 'මිනිමාටා සංකල්පයේ ක්‍රියාත්මක කිරීම')}
                  </h4>
                </div>
              </div>
              <div className="bg-white/60 rounded-lg md:rounded-xl p-4 md:p-6 border border-white/40">
                <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                  <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-secondary-blue flex-shrink-0" />
                  <h4 className="text-sm md:text-base font-semibold text-text-dark">
                    {t('e-waste', 'E-Waste Management', 'E-අපද්‍රව්‍ය කළමනාකරණය')}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Our Unique Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center"
        >
          <h3 className="text-xl sm:text-2xl font-bold text-text-dark mb-6 md:mb-8">
            {t('unique-values', 'Our Unique Values & Committees', 'අපගේ අද්විතීය වටිනාකම් සහ කමිටු')}
          </h3>
          <div className="bg-white rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xl border border-gray-100">
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {uniqueValues.map((value, index) => (
                <motion.div
                  key={value}
                  className="bg-gradient-to-r from-primary-green/10 to-secondary-blue/10 border border-primary-green/20 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-text-dark hover:from-primary-green/20 hover:to-secondary-blue/20 transition-all duration-300"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                >
                  {t(`value-${value.toLowerCase().replace(/\s+/g, '-').replace(/"/g, '')}`, value, uniqueValuesSinhala[index])}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
